import { focusElement } from '../dom-utils';
import { focusFirstIn, getActiveElement, getNextTabbableIndex, isElementInScope, isFocusFree, isGuard, } from './utils';
import { getTabbables } from './utils/tabbable';
var Direction;
(function (Direction) {
    Direction[Direction["None"] = 0] = "None";
    Direction[Direction["Forward"] = 1] = "Forward";
    Direction[Direction["Backward"] = -1] = "Backward";
})(Direction || (Direction = {}));
var FocusTrapManager = /** @class */ (function () {
    function FocusTrapManager() {
        this.instances = [];
        this.direction = Direction.None;
        this.lastActiveElement = null;
        this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
        this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
        this.onMouseDown = this.onMouseDown.bind(this);
    }
    FocusTrapManager.getInstance = function () {
        if (!this.manager) {
            this.manager = new FocusTrapManager();
        }
        return this.manager;
    };
    FocusTrapManager.prototype.activate = function (instance) {
        this.preventLastFocusIn();
        if (this.instances.length === 0) {
            this.attachEvents();
        }
        this.instances.push(instance);
        this.autoFocus(instance);
    };
    FocusTrapManager.prototype.deactivate = function (instance) {
        this.preventLastFocusIn();
        this.instances = this.instances.filter(function (trap) { return trap !== instance; });
        if (this.instances.length === 0) {
            this.detachEvents();
        }
    };
    FocusTrapManager.prototype.autoFocus = function (instance) {
        var scope = instance.getScope();
        var autoFocus = instance.getOptions().autoFocus;
        var activeElement = getActiveElement();
        if (autoFocus && (!activeElement || !isElementInScope(scope, activeElement))) {
            focusFirstIn(scope);
            // Set focus for modal container when not found focusable element inside.
            this.focusScope(scope);
        }
    };
    FocusTrapManager.prototype.focusScope = function (scope) {
        var activeElement = getActiveElement();
        if (!activeElement || !isElementInScope(scope, activeElement)) {
            focusElement(scope, true);
        }
    };
    FocusTrapManager.prototype.checkFocusInCurrentScope = function () {
        var instance = this.instances[this.instances.length - 1];
        if (instance) {
            this.checkFocusIn(instance);
        }
    };
    FocusTrapManager.prototype.preventLastFocusIn = function () {
        if (this.raf) {
            cancelAnimationFrame(this.raf);
        }
    };
    FocusTrapManager.prototype.checkFocusIn = function (instance) {
        var scope = instance.getScope();
        var _a = this, direction = _a.direction, lastActiveElement = _a.lastActiveElement;
        var activeElement = getActiveElement();
        this.lastActiveElement = activeElement;
        this.direction = Direction.None;
        if (!activeElement || isFocusFree() || isElementInScope(scope, activeElement)) {
            return;
        }
        var tabbables = getTabbables(scope, function (node) { return !isGuard(node); });
        var activeIndex = tabbables.indexOf(activeElement);
        if (tabbables.length === 0) {
            this.focusScope(scope);
        }
        else if (activeIndex === -1) {
            var currentIndex = lastActiveElement ? tabbables.indexOf(lastActiveElement) : -1;
            var index = getNextTabbableIndex(currentIndex, tabbables.length, direction);
            focusElement(tabbables[index]);
        }
    };
    FocusTrapManager.prototype.attachEvents = function () {
        document.addEventListener('focusin', this.onDocumentFocusIn, true);
        document.addEventListener('keydown', this.onDocumentKeyDown, true);
        document.addEventListener('mousedown', this.onMouseDown, true);
    };
    FocusTrapManager.prototype.detachEvents = function () {
        document.removeEventListener('focusin', this.onDocumentFocusIn, true);
        document.removeEventListener('keydown', this.onDocumentKeyDown, true);
        document.removeEventListener('mousedown', this.onMouseDown, true);
    };
    FocusTrapManager.prototype.onDocumentFocusIn = function () {
        var _this = this;
        // Use requestAnimationFrame for avoid problems with focus when activates new focus trap.
        this.raf = requestAnimationFrame(function () {
            _this.checkFocusInCurrentScope();
        });
    };
    FocusTrapManager.prototype.onDocumentKeyDown = function (event) {
        if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }
        this.direction = event.shiftKey ? Direction.Backward : Direction.Forward;
    };
    FocusTrapManager.prototype.onMouseDown = function () {
        this.direction = Direction.None;
    };
    return FocusTrapManager;
}());
export { FocusTrapManager };
